import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { money, ORDER_STATUS_LABELS, CATEGORY_LABELS } from "@/config";

const STATUSES = Object.keys(ORDER_STATUS_LABELS);

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (orderNumber, status) => {
    try {
      await api.patch(`/admin/orders/${orderNumber}/status`, { status });
      toast.success(`Pedido ${orderNumber} → ${ORDER_STATUS_LABELS[status]}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="overflow-x-auto rounded-2xl border border-sand bg-white" data-testid="admin-orders-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Pedido</TableHead><TableHead>Cliente</TableHead><TableHead>Entrega</TableHead>
            <TableHead>Productos</TableHead><TableHead>Total</TableHead><TableHead>Pago</TableHead><TableHead>Estado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((o) => (
            <TableRow key={o.order_number} data-testid={`admin-order-${o.order_number}`}>
              <TableCell className="font-bold">
                {o.order_number}
                {o.confirmation_email_status === "email_failed" && (
                  <span className="mt-1 block w-fit rounded-full bg-habanero/10 px-2 py-0.5 text-[10px] font-bold text-habanero" data-testid={`order-email-failed-${o.order_number}`}>Correo falló</span>
                )}
              </TableCell>
              <TableCell>
                <p className="font-semibold">{o.customer.name}</p>
                <p className="text-xs text-clay">{o.customer.email} · {o.customer.phone}</p>
              </TableCell>
              <TableCell className="text-sm">{o.delivery_method === "pickup" ? "Pick up" : "Envío"}</TableCell>
              <TableCell className="max-w-48 text-xs">
                {o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}
              </TableCell>
              <TableCell className="font-bold">{money(o.total)}</TableCell>
              <TableCell>
                <span className={`rounded-full px-2 py-1 text-xs font-bold ${o.payment_status === "paid" ? "bg-leaf/15 text-leaf" : "bg-corn/30 text-cocoa"}`}>
                  {o.payment_status === "paid" ? "Pagado" : "Pendiente"}
                </span>
              </TableCell>
              <TableCell>
                <Select value={o.status} onValueChange={(v) => setStatus(o.order_number, v)}>
                  <SelectTrigger className="w-44 border-sand" data-testid={`admin-status-${o.order_number}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{ORDER_STATUS_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
          {orders.length === 0 && (
            <TableRow><TableCell colSpan={7} className="py-10 text-center font-script font-semibold text-2xl text-clay">Aún no hay pedidos</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ProductDialog({ product, onClose, onSaved }) {
  const isNew = !product?.slug;
  const [form, setForm] = useState(() => ({
    name: product?.name || "",
    category: product?.category || "salsa",
    spice_level: product?.spice_level ?? 1,
    short: product?.short || "",
    description: product?.description || "",
    ingredients: (product?.ingredients || []).join(", "),
    image: product?.image || "",
    featured: product?.featured || false,
  }));
  const [variants, setVariants] = useState(() =>
    product?.variants?.length
      ? product.variants.map((v) => ({ ...v }))
      : [{ id: "250", label: "Frasco 250 ml", price: "", stock: "" }]
  );
  const [saving, setSaving] = useState(false);

  const setVariant = (i, k, val) =>
    setVariants((vs) => vs.map((v, j) => (j === i ? { ...v, [k]: val } : v)));

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      spice_level: Number(form.spice_level),
      ingredients: form.ingredients.split(",").map((s) => s.trim()).filter(Boolean),
      variants: variants.map((v, i) => ({
        id: v.id || `v${Date.now()}${i}`,
        label: v.label,
        price: Number(v.price),
        stock: Number(v.stock),
      })),
    };
    try {
      if (isNew) await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${product.slug}`, payload);
      toast.success(isNew ? "Producto creado" : "Producto actualizado");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  const invalid = !form.name || variants.some((v) => !v.label || v.price === "" || v.stock === "");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-cream sm:max-w-lg" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-cocoa">{isNew ? "Nuevo producto" : `Editar ${product.name}`}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div>
            <Label>Nombre</Label>
            <Input data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 border-sand bg-white" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1 border-sand bg-white" data-testid="product-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-pican">Los que no pican</SelectItem>
                  <SelectItem value="machas">Las Machas</SelectItem>
                  <SelectItem value="habaneros">Los Habaneros</SelectItem>
                  <SelectItem value="variedad">La Variedad</SelectItem>
                  <SelectItem value="kit">Kit especial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nivel de picor (1-5)</Label>
              <Input type="number" min="1" max="5" data-testid="product-form-spice" value={form.spice_level} onChange={(e) => setForm({ ...form, spice_level: e.target.value })} className="mt-1 border-sand bg-white" />
            </div>
          </div>
          <div>
            <Label>Descripción corta</Label>
            <Input data-testid="product-form-short" value={form.short} onChange={(e) => setForm({ ...form, short: e.target.value })} className="mt-1 border-sand bg-white" />
          </div>
          <div>
            <Label>Descripción</Label>
            <Textarea data-testid="product-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 border-sand bg-white" rows={3} />
          </div>
          <div>
            <Label>Ingredientes (separados por coma)</Label>
            <Input data-testid="product-form-ingredients" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} className="mt-1 border-sand bg-white" />
          </div>
          <div>
            <Label>URL de imagen</Label>
            <Input data-testid="product-form-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="mt-1 border-sand bg-white" />
          </div>
          <div>
            <Label>Presentaciones</Label>
            <div className="mt-2 space-y-2">
              {variants.map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input placeholder="Etiqueta (ej. Frasco 250 ml)" value={v.label} onChange={(e) => setVariant(i, "label", e.target.value)} className="border-sand bg-white" data-testid={`variant-label-${i}`} />
                  <Input type="number" step="0.01" placeholder="Precio" value={v.price} onChange={(e) => setVariant(i, "price", e.target.value)} className="w-24 border-sand bg-white" data-testid={`variant-price-${i}`} />
                  <Input type="number" placeholder="Stock" value={v.stock} onChange={(e) => setVariant(i, "stock", e.target.value)} className="w-20 border-sand bg-white" data-testid={`variant-stock-${i}`} />
                  <Button variant="ghost" size="icon" disabled={variants.length <= 1} onClick={() => setVariants((vs) => vs.filter((_, j) => j !== i))} aria-label="Quitar presentación" data-testid={`variant-remove-${i}`}>
                    <Trash2 size={15} className="text-destructive" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setVariants((vs) => [...vs, { id: `v${Date.now()}`, label: "", price: "", stock: "" }])} className="rounded-full border-sand text-cocoa" data-testid="variant-add-button">
                <Plus size={14} className="mr-1" /> Agregar presentación
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="product-form-featured" />
            <Label>Destacado en la página de inicio</Label>
          </div>
          <Button onClick={save} disabled={saving || invalid} data-testid="product-form-save" className="rounded-full bg-habanero py-6 font-bold text-white hover:bg-habanero-dark">
            {saving ? "Guardando..." : "Guardar producto"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = () => api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (slug) => {
    try {
      await api.delete(`/admin/products/${slug}`);
      toast.success("Producto eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing(null)} data-testid="admin-new-product-button" className="rounded-full bg-habanero font-bold text-white hover:bg-habanero-dark">
          <Plus className="mr-2" size={16} /> Nuevo producto
        </Button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-sand bg-white" data-testid="admin-products-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Producto</TableHead><TableHead>Categoría</TableHead><TableHead>Picor</TableHead>
              <TableHead>Presentaciones</TableHead><TableHead>Stock</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.slug} data-testid={`admin-product-${p.slug}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt={p.name} className="h-10 w-10 rounded-lg border border-sand object-cover" />
                    <span className="font-bold">{p.name}{p.featured && <span className="ml-2 rounded-full bg-corn px-2 py-0.5 text-[10px] font-bold">DESTACADO</span>}</span>
                  </div>
                </TableCell>
                <TableCell>{CATEGORY_LABELS[p.category] || p.category}</TableCell>
                <TableCell>{p.spice_level}/5</TableCell>
                <TableCell className="text-xs">{p.variants.map((v) => `${v.label}: ${money(v.price)}`).join(" · ")}</TableCell>
                <TableCell>{p.variants.map((v) => v.stock).join(" / ")}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" data-testid={`admin-edit-${p.slug}`} aria-label={`Editar ${p.name}`} onClick={() => setEditing(p)}>
                      <Pencil size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`admin-delete-${p.slug}`} aria-label={`Eliminar ${p.name}`} onClick={() => remove(p.slug)}>
                      <Trash2 size={16} className="text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {editing !== undefined && (
        <ProductDialog product={editing} onClose={() => setEditing(undefined)} onSaved={load} />
      )}
    </div>
  );
}

function QuotesTab() {
  const [quotes, setQuotes] = useState([]);
  useEffect(() => { api.get("/admin/quotes").then(({ data }) => setQuotes(data)).catch(() => {}); }, []);

  return (
    <div className="space-y-4" data-testid="admin-quotes-list">
      {quotes.length === 0 && <p className="rounded-2xl border border-sand bg-white py-10 text-center font-script font-semibold text-2xl text-clay">Sin cotizaciones pendientes</p>}
      {quotes.map((q) => (
        <div key={q.quote_id} className="rounded-2xl border border-sand bg-white p-6" data-testid={`admin-quote-${q.quote_id}`}>
          <div className="flex flex-wrap justify-between gap-2">
            <p className="font-bold text-cocoa">{q.name} {q.business && <span className="text-clay">· {q.business}</span>}</p>
            <div className="flex items-center gap-2">
              {q.email_status === "email_sent" && <span className="rounded-full bg-leaf/15 px-2 py-1 text-xs font-bold text-leaf" data-testid={`quote-email-sent-${q.quote_id}`}>Correo enviado</span>}
              {q.email_status === "email_failed" && <span className="rounded-full bg-habanero/10 px-2 py-1 text-xs font-bold text-habanero" data-testid={`quote-email-failed-${q.quote_id}`}>Correo falló</span>}
              <p className="text-xs text-clay">{new Date(q.created_at).toLocaleDateString("es-MX")}</p>
            </div>
          </div>
          <p className="mt-1 text-sm text-clay">{q.email} · {q.phone}{q.tipo ? ` · Cotiza por ${q.tipo}` : ""}</p>
          {q.products && <p className="mt-2 text-sm"><strong>Productos:</strong> {q.products}</p>}
          {q.quantity && <p className="text-sm"><strong>Cantidad:</strong> {q.quantity}</p>}
          {q.message && <p className="mt-2 text-sm text-clay">{q.message}</p>}
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();

  if (user === null)
    return <div className="flex min-h-[50vh] items-center justify-center"><Flame className="animate-pulse text-habanero" size={40} /></div>;

  if (!user || user.role !== "admin")
    return (
      <div className="mx-auto max-w-md px-4 py-32 text-center" data-testid="admin-no-access">
        <p className="font-script font-semibold text-4xl text-clay">Esta cocina es solo para la familia</p>
        <p className="mt-3 text-sm text-clay">Necesitas una cuenta de administrador.</p>
        <Button asChild className="mt-6 rounded-full bg-habanero px-8 font-bold text-white hover:bg-habanero-dark" data-testid="admin-login-link">
          <Link to="/cuenta">Ir a iniciar sesión</Link>
        </Button>
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8" data-testid="admin-page">
      <p className="font-script font-semibold text-2xl text-orange">La cocina de SoyDiVa</p>
      <h1 className="mt-1 font-script text-4xl font-semibold text-cocoa">Panel de administración</h1>
      <Tabs defaultValue="orders" className="mt-10">
        <TabsList className="bg-surface">
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Pedidos</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-tab-products">Productos</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Cotizaciones</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-6"><OrdersTab /></TabsContent>
        <TabsContent value="products" className="mt-6"><ProductsTab /></TabsContent>
        <TabsContent value="quotes" className="mt-6"><QuotesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
